import { hasAccessRight } from "../shared/auth/accessRights";
import type { MessageKey } from "../shared/i18n/messages.de";
import { routePaths } from "./navigation";

/** A function of the app that a role without own records can use, with the way to it. */
export interface RoleFunction {
  /** Label message key. */
  readonly labelKey: MessageKey;
  /** Path including the section of the page. */
  readonly path: string;
}

/** What the start page offers people without own records and without a screen of their own. */
export interface RoleOverview {
  /** Functions in the app, in display order; the privacy information is always available. */
  readonly functions: readonly RoleFunction[];
  /** Rights of the role that the app has no screen for, as message keys of an explanation. */
  readonly notInApp: readonly MessageKey[];
}

/**
 * Lists what the app offers a role whose functions all sit on „Datenschutz“, such as auditors and
 * works councils (UX-026, spec 2.2): the audit chain check, the configuration export for the works
 * agreement and the privacy information, plus the rights the app has no screen for (authority
 * exports and records of single persons, aggregated reports), which the start page names instead of
 * leaving the person on a page without explanation.
 *
 * @param roles - Roles of the person.
 * @returns The overview.
 */
export function roleOverviewFor(roles: readonly string[]): RoleOverview {
  const functions: RoleFunction[] = [];
  const notInApp: MessageKey[] = [];

  if (hasAccessRight(roles, "VerifyAuditLog")) {
    functions.push({
      labelKey: "privacy.auditChain",
      path: `${routePaths.privacy}#privacy-audit-chain`,
    });
  }

  if (hasAccessRight(roles, "ReadConfigurationExport")) {
    functions.push({
      labelKey: "privacy.configuration",
      path: `${routePaths.privacy}#privacy-configuration`,
    });
  }

  functions.push({
    labelKey: "start.overview.privacy",
    path: `${routePaths.privacy}#privacy-info`,
  });

  const readsRecords =
    hasAccessRight(roles, "ExportAuthorityRecords") || hasAccessRight(roles, "ReadEmployeeTimes");

  if (readsRecords && !hasAccessRight(roles, "ManageMasterData")) {
    notInApp.push("start.overview.records");
  }

  if (hasAccessRight(roles, "ReadAggregatedReports")) {
    notInApp.push("start.overview.reports");
  }

  const overview: RoleOverview = { functions, notInApp };

  return overview;
}
